import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Popover } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import i18n from 'i18next';
import React, { useMemo, useRef, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CertificateFrontContent from 'commons/Certificate/components/CertificateFrontContent';
import DownloadCertificateLanguageDialog from 'commons/Certificate/components/DownloadCertificateLanguageDialog';
import { exportCertificatePdf, waitForNextPaint } from 'commons/Certificate/utils/certificatePdf';
import CertificateBack from 'commons/CertificateBack';
import DialogCustom from 'commons/DialogCustom';
import Text from 'commons/Text';
import API from 'configs/API';
import Colors from 'configs/Colors';
import { TESTID } from 'configs/Constant';
import useDeleteArtwork from 'hooks/useDeleteArtwork';
import useOwnedEditions from 'hooks/useOwnedEditions';
import useRole from 'hooks/useRole';
import Routes from 'utils/Route';
import { ToastTopHelper } from 'utils/utils';

import useStyles from './styles';

const ArtworkItemPopover = ({ artwork, status, dotStyle }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutate: deleteArtwork } = useDeleteArtwork();
  const [isShowDialog, setIsShowDialog] = useState(false);
  const { isArtist, isCollectorOrGalleryOwner } = useRole();
  const queryClient = useQueryClient();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isSelectLanguageOpen, setIsSelectLanguageOpen] = useState(false);
  const [downloadLanguage, setDownloadLanguage] = useState('en');
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [exportRenderKey, setExportRenderKey] = useState(0);

  const certificateExportFrontRef = useRef(null);
  const certificateBackRef = useRef(null);
  const exportI18nRef = useRef(i18n.cloneInstance({ initImmediate: false }));

  const ownedEditions = useOwnedEditions(artwork);
  const hasEditionsData = Array.isArray(artwork?.editions) && artwork.editions.length > 0;

  const dialogOwnedEditions = useMemo(() => {
    if (hasEditionsData) {
      if (isArtist) {
        const artistEditions = artwork.editions
          .filter((ed) => ed?.id_certificate !== null)
          .map((ed) => ({ ...ed, total_edition: artwork?.total_edition }));
        return artistEditions.length > 0 ? artistEditions : null;
      }
      if (ownedEditions.length === 0) return [];
      return ownedEditions.map((ed) => ({ ...ed, total_edition: artwork?.total_edition }));
    }
    if (artwork?.certificate?.edition_number != null) {
      return [
        {
          id: artwork.certificate.id,
          edition_number: artwork.certificate.edition_number,
          total_edition: artwork.total_edition,
        },
      ];
    }
    return null;
  }, [isArtist, hasEditionsData, artwork, ownedEditions]);

  // Build the certificate used for the PDF export (reflects the chosen edition)
  const downloadCertificate = useMemo(() => {
    if (selectedEdition) {
      return { ...artwork?.certificate, edition_number: selectedEdition.edition_number };
    }
    return artwork?.certificate;
  }, [selectedEdition, artwork?.certificate]);

  const isStatusScanRequestCertificate =
    Array.isArray(status) && status?.find((item) => item?.value === artwork?.status?.value);

  const numberEditionHadCer = artwork?.editions?.reduce((acc, edition) => {
    return acc + (edition?.id_certificate !== null ? 1 : 0);
  }, 0);
  const isAllEditionHadCer = numberEditionHadCer === artwork?.total_edition;

  const canRequestCertificate = () => {
    return isStatusScanRequestCertificate && !isAllEditionHadCer;
  };

  // Show download when certificate exists.
  // Artists always qualify; collectors require edition ownership when editions data is available.
  const canDownloadCertificate = artwork?.certificate;

  const handleOpenPopover = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleOpenSelectLanguageDialog = (e) => {
    e.stopPropagation();
    if (!canDownloadCertificate || isDownloading) {
      return;
    }

    setAnchorEl(null);
    setDownloadLanguage(i18n.language || 'en');
    setSelectedEdition(dialogOwnedEditions?.length === 1 ? dialogOwnedEditions[0] : null);
    setIsSelectLanguageOpen(true);
  };

  const handleCloseSelectLanguageDialog = () => {
    if (isDownloading) {
      return;
    }

    setIsSelectLanguageOpen(false);
    setSelectedEdition(null);
  };

  const handleConfirmDownload = async () => {
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    setIsSelectLanguageOpen(false);

    try {
      if (downloadLanguage === 'both') {
        await exportI18nRef.current.changeLanguage('vi');
        setExportRenderKey((prevKey) => prevKey + 1);
        await waitForNextPaint();
        await exportCertificatePdf({
          frontElement: certificateExportFrontRef.current,
          backElement: certificateBackRef.current,
          fileName: 'certificate_vn',
        });

        await exportI18nRef.current.changeLanguage('en');
        setExportRenderKey((prevKey) => prevKey + 1);
        await waitForNextPaint();
        await exportCertificatePdf({
          frontElement: certificateExportFrontRef.current,
          backElement: certificateBackRef.current,
          fileName: 'certificate_en',
        });
        return;
      }

      await exportI18nRef.current.changeLanguage(downloadLanguage);
      setExportRenderKey((prevKey) => prevKey + 1);
      await waitForNextPaint();
      const singleDownloadFileName =
        downloadLanguage === 'vi' ? 'certificate_vn' : 'certificate_en';
      await exportCertificatePdf({
        frontElement: certificateExportFrontRef.current,
        backElement: certificateBackRef.current,
        fileName: singleDownloadFileName,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRequestCertificate = (e) => {
    e.stopPropagation();
    if (isArtist) {
      navigate(Routes.CreateCertificate(artwork?.uuid));
    } else if (isCollectorOrGalleryOwner) {
      navigate(Routes.RequestCertificateId(artwork?.uuid));
    }
  };

  const handleShowPopUpConfirmDelete = (e) => {
    e.stopPropagation();
    setAnchorEl(null);
    setIsShowDialog(true);
  };

  const handleDeleteArtwork = (e) => {
    e.stopPropagation();
    deleteArtwork(artwork?.uuid, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [API.ARTWORK.LIST_GALLERY_ARTWORK] });
        ToastTopHelper.success(
          t(
            artwork?.has_certificate ? 'text_move_artwork_to_store' : 'text_delete_artwork_success',
          ),
        );
      },
    });

    setIsShowDialog(false);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setIsShowDialog(false);
  };

  const handleNavigateEditArtwork = (e) => {
    e.stopPropagation();
    navigate(Routes.EditArtwork(artwork?.uuid));
  };

  const certificateClasses = {
    cornerTopLeft: classes.certificateCornerTopLeft,
    cornerTopRight: classes.certificateCornerTopRight,
    cornerBottomLeft: classes.certificateCornerBottomLeft,
    cornerBottomRight: classes.certificateCornerBottomRight,
    line: classes.certificateLine,
    text_certificate_page_1: classes.certificateTextCertificatePage1,
  };

  return (
    <>
      <I18nextProvider
        key={exportRenderKey}
        i18n={exportI18nRef.current}
      >
        <div className={classes.exportWrapper}>
          <div
            ref={certificateExportFrontRef}
            className={classes.certificateExportPage}
          >
            <CertificateFrontContent
              artwork={artwork}
              certificate={downloadCertificate}
              classes={certificateClasses}
            />
          </div>
          <div ref={certificateBackRef}>
            <CertificateBack />
          </div>
        </div>
      </I18nextProvider>

      <div
        className={clsx(
          classes.wrapdots,
          {
            [classes.dotActive]: !!anchorEl,
          },
          dotStyle,
        )}
        onClick={handleOpenPopover}
        testid={TESTID.BUTTON_ARTWORK_ITEM_POPOVER}
      >
        <MoreHorizIcon className={classes.iconDots} />
      </div>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        className={classes.popover}
        testid={TESTID.POPOVER}
      >
        {isCollectorOrGalleryOwner && (
          <div
            className={clsx(classes.wrapPopoverContent, {
              [classes.disable]: !canRequestCertificate(),
            })}
            onClick={canRequestCertificate() ? handleRequestCertificate : undefined}
            testid={`${TESTID.REQUEST_CERTIFICATE}-${artwork?.id}`}
          >
            <Text>{t('request_certificate')}</Text>
          </div>
        )}

        <div
          className={clsx(classes.wrapPopoverContent, {
            [classes.disable]: !canDownloadCertificate,
          })}
          onClick={canDownloadCertificate ? handleOpenSelectLanguageDialog : undefined}
        >
          <Text>{t('download_certificate')}</Text>
        </div>

        <div
          className={classes.wrapPopoverContent}
          onClick={handleNavigateEditArtwork}
        >
          <Text>{t('edit_artwork')}</Text>
        </div>

        <div
          className={classes.wrapPopoverContent}
          onClick={handleShowPopUpConfirmDelete}
        >
          <Text color={Colors.Error}>{t('delete_artwork')}</Text>
        </div>
      </Popover>

      <DialogCustom
        isShowDialog={isShowDialog}
        dialogTitle={t('delete_artwork')}
        textCancel={t('cancel')}
        textSubmit={t('delete')}
        onCancel={handleCancelDelete}
        onSubmit={handleDeleteArtwork}
        submitButtonStyle={clsx(classes.buttonDialog, classes.buttonDanger)}
        cancelButtonStyle={clsx(classes.buttonDialog)}
      >
        <div className={classes.wrapDialogContent}>
          {t(
            artwork?.has_certificate
              ? 'text_confirm_delete_artwork_has_certificate'
              : 'text_confirm_delete_artwork_no_certificate',
          )}
        </div>
      </DialogCustom>

      <DownloadCertificateLanguageDialog
        isOpen={isSelectLanguageOpen}
        selectedLanguage={downloadLanguage}
        selectedEdition={selectedEdition}
        ownedEditions={dialogOwnedEditions}
        onClose={handleCloseSelectLanguageDialog}
        onChangeLanguage={setDownloadLanguage}
        onChangeEdition={setSelectedEdition}
        onConfirm={handleConfirmDownload}
        isDownloading={isDownloading}
      />
    </>
  );
};

export default ArtworkItemPopover;
